<template>
	<div>
		<DetailsLayout
			v-if="!fetchCategoryError"
			:title="category.title"
			:label="$t('admin_categories_groups_label')"
			:buttonText="$t('admin_groups_new_group_button_title')"
			:placeholder="
				$t('admin_categories_search_groups_action', {
					CategoryName: category.title,
				})
			"
			:loading="fetchingCategoryDetails"
			class="category-details"
			@click="showNewGroup = true"
			@search="onSearch"
		>
			<template #table>
				<Table
					:columns="columns"
					:rows="groups"
					clickable
					infinite
					localize
					closePopoversOnScroll
					:loadingOverlay="fetchingGroups"
					:error="fetchGroupsError"
					headerSize="small"
					@row-click="goToGroupDetails"
					height="calc(100vh - 296px)"
					ref="table"
					@sort="onSort"
				>
					<template #cell-created_at="{ value }">
						{{ $m(value).format('L') }}
					</template>
					<template #cell-latest_user_message_created_at="{ value }">
						{{
							value
								? timeFromNow(value)
								: $t('groupsettings_option_category_uncatagorized')
						}}
					</template>

					<template #cell-memberships_count="{ value }">
						{{ value }}
					</template>

					<template #cell-actions="{ data }">
						<DotsMenu
							localize
							:items="menuOptions"
							@click.stop
							@select="onChatMenuItemClick($event, data)"
						/>
					</template>

					<template #empty>
						<EmptyState
							v-if="!query"
							:icon="GroupsIcon"
							:text="$t('admin_groups_create_spaces_title')"
							:buttonText="$t('admin_groups_new_group_button_title')"
							@click="showNewGroup = true"
						/>
						<NoSearchResults v-if="query" :query="query" />
					</template>
				</Table>
			</template>
			<template #sidebar>
				<SidebarSection>
					<InfoList
						localize
						:items="infoItems"
						title="admin_categories_category_info_label"
					/>
				</SidebarSection>
				<SidebarSection>
					<ActionList
						localize
						:items="actionItems"
						title="admin_groups_actions_label"
						@select="onSelectAction"
					/>
				</SidebarSection>
				<SidebarSection v-if="category.locked">
					<ActionList localize :items="lockedItems" @select="onSelectAction" />
				</SidebarSection>
			</template>
		</DetailsLayout>

		<RenameCategoryModal
			v-if="renameCategory"
			:category="category"
			@close="renameCategory = false"
			@success="updateCategory"
		/>

		<DeleteCategoryModal
			v-if="deleteCategory"
			:category="category"
			@close="deleteCategory = false"
			@delete="exitDetail"
		/>

		<NewGroupModal
			v-if="showNewGroup"
			@success="onNewGroupSuccess"
			@close="showNewGroup = false"
			:defaultCategoryId="parseInt(categoryId)"
		/>

		<FullPageError @retry="fetchRetry" v-if="fetchCategoryError" />
	</div>
</template>

<script>
import { Button, Table, ASC, DotsMenu } from '@/components/common'
import { GroupsIcon, PencilIcon, TrashCanIcon, LockIcon } from '@/icons'
import {
	InfoList,
	ActionList,
	DetailsLayout,
	SidebarSection,
	EmptyState,
	NoSearchResults,
	get,
	FullPageError,
} from '../common'
import NewGroupModal from '../Groups/NewGroupModal'
import RenameCategoryModal from './RenameCategoryModal'
import DeleteCategoryModal from './DeleteCategoryModal'
import { canRetryAPIError, getTimeAgoShorthand } from '@/helpers/utils'
import debounce from 'debounce'

const PAGE_SIZE = 30

const CategoryDetails = {
	name: 'CategoryDetails',
	components: {
		Button,
		Table,
		InfoList,
		ActionList,
		DotsMenu,
		DetailsLayout,
		SidebarSection,
		EmptyState,
		NoSearchResults,
		RenameCategoryModal,
		DeleteCategoryModal,
		NewGroupModal,
		FullPageError,
	},
	props: { categoryId: [Number, String] },
	data() {
		return {
			category: {},
			groups: [],
			query: '',
			loading: true,
			GroupsIcon,
			showNewGroup: false,
			renameCategory: false,
			deleteCategory: false,
			fetchCategoryError: false,
			fetchingCategoryDetails: true,
			fetchGroupsError: false,
			fetchingGroups: true,
			columns: [
				{
					field: 'title',
					label: 'admin_groups_group_name_title',
					sorter: true,
					ascSort: 'title',
					dscSort: '-title',
				},
				{
					field: 'created_at',
					label: 'admin_categories_created_on_label',
					width: '400px',
					sorter: true,
					ascSort: 'created_at',
					dscSort: '-created_at',
				},
				{
					field: 'latest_user_message_created_at',
					label: 'admin_groups_message_sent_label',
					width: '600px',
				},
				{
					field: 'memberships_count',
					label: 'admin_groups_group_members_title',
					width: '600px',
				},
				{
					field: 'actions',
					align: 'right',
					width: '100px',
				},
			],
			menuOptions: [
				{
					id: 'details',
					label: 'admin_groups_open_group_details_button',
				},
				{ id: 'edit', label: 'admin_groups_edit_group_button' },
			],
			sort: { field: 'group', order: ASC, param: 'title' },
		}
	},

	created() {
		this.fetchGroupsDebounced = debounce(this.fetchGroups.bind(this), 500)
	},

	mounted() {
		this.fetchCategoryDetails()
		this.fetchGroups()
	},
	computed: {
		actionItems() {
			return [
				{
					id: 'rename',
					icon: PencilIcon,
					label: 'admin_categories_rename_category_action',
					disabled: this.category.locked || false,
				},
				{
					id: 'delete',
					icon: TrashCanIcon,
					type: 'danger',
					label: 'admin_categories_delete_category_button',
					disabled: this.category.locked || false,
				},
			]
		},
		infoItems() {
			return [
				{
					label: 'admin_categories_category_id_label',
					value: this.category.id,
				},
				{
					label: 'admin_categories_created_on_label',
					value: this.$m(this.category.created_at).format('L'),
				},
				{
					label: 'admin_categories_groups_label',
					value: this.category.chats_count,
				},
			]
		},
		lockedItems() {
			return [
				{
					id: 'delete',
					icon: LockIcon,
					type: 'primary',
					label: 'admin_categories_category_locked_label',
				},
			]
		},
	},
	methods: {
		onNewGroupSuccess() {
			this.fetchGroups()
			this.fetchCategoryDetails()
		},
		onSearch(query) {
			this.query = query || null
			this.fetchingGroups = true
			this.fetchGroupsDebounced()
		},
		goToGroupDetails({ id }) {
			this.$router.push({ name: 'group', params: { id } })
		},
		onChatMenuItemClick({ id }, chat) {
			switch (id) {
				case 'edit':
					this.goToEditChat(chat)
					break
				case 'details':
					this.goToChatDetails(chat)
					break
			}
		},
		onSelectAction({ id }) {
			switch (id) {
				case 'rename':
					this.renameCategory = true
					break
				case 'delete':
					this.deleteCategory = true
					break
			}
		},
		goToChatDetails(chat) {
			this.$router.push({
				name: 'group',
				params: { id: chat.id },
			})
		},
		goToEditChat(chat) {
			this.$router.push({
				name: 'group',
				params: { id: chat.id },
				query: { edit: true },
			})
		},

		fetchRetry() {
			this.fetchCategoryDetails()
		},

		async fetchCategoryDetails() {
			this.fetchCategoryError = false
			this.fetchingCategoryDetails = true
			try {
				const apiResponse = await get(
					`/organization/categories/${this.categoryId}`
				)
				this.category = apiResponse.data
				this.fetchingCategoryDetails = false
			} catch (e) {
				this.fetchingCategoryDetails = false
				this.$alert.error(this.$t('common_api_error_save'))
				if (canRetryAPIError(e)) {
					// if a retry is possible, show fullpage error
					this.fetchCategoryError = true
				} else {
					// otherwise route to category list page
					this.$router.push('/organization/categories')
				}
			}
		},

		async fetchGroups() {
			this.fetchGroupsError = false
			this.fetchingGroups = true
			const { param: sort } = this.sort
			const params = {
				'page[size]': PAGE_SIZE,
				'filter[category.id]': this.categoryId,
				sort,
			}
			if (this.query) {
				params['filter[search]'] = this.query
			}
			try {
				this.apiResponse = await get(`/organization/groups`, params)
				this.groups = this.apiResponse.data
				this.fetchingGroups = false
				this.$refs.table.scrollToTop()
			} catch (e) {
				console.error(e)
				this.fetchGroupsError = true
				this.fetchingGroups = false
				this.$alert.error(this.$t('common_api_error_save'))
			}
		},

		timeFromNow(dateStr) {
			const time = getTimeAgoShorthand(dateStr)
			return this.$t('announcements_daterelative', [time])
		},

		updateCategory(category) {
			this.category = category
		},

		onSort(sort, { ascSort, dscSort }) {
			this.sort = {
				...sort,
				param: sort.order === ASC ? ascSort : dscSort,
			}
			this.fetchGroups()
		},

		exitDetail() {
			this.$router.push('/organization/categories')
		},
	},
}

export default CategoryDetails
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

.category-details {
	display: grid;
	grid-template-columns: auto 240px;
	grid-template-rows: 125px 135px auto;
	grid-template-areas:
		'header header'
		'content sidebar'
		'table sidebar';
	height: 100vh;
	width: 100%;
	background: #fafafa;
	.details-header {
		grid-area: header;
		padding: 24px;
		border-bottom: 1px solid var(--neutral-200);
		> h1 {
			font-size: 36px;
			color: var(--neutral-800);
			margin: 16px 0 0 0;
			font-family: Avenir-Heavy;
		}
	}
	.details-content {
		grid-area: content;
		padding: 8px 24px;
		@include flex-column;
		justify-content: center;
		.search-bar {
			width: 100%;
			background: white;
		}
		.details-content-top {
			font-size: 24px;
			font-family: Avenir-Heavy;
			margin-bottom: 16px;
			.pronto-btn {
				margin-left: 16px;
			}
		}
	}
	.details-table {
		grid-area: table;
	}
	.details-sidebar {
		grid-area: sidebar;
		border-left: 1px solid var(--neutral-200);

		.details-sidebar-section {
			padding: 16px 12px;
			border-bottom: 1px solid var(--neutral-200);
			&:last-child {
				border-bottom: 0;
			}
		}
	}
}
</style>
